import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { ArrowLeft, Plus, Home, Building, AlertCircle, CheckCircle, Clock, Trash2, Edit, Eye, Lock } from "lucide-react";
import { toast } from "sonner";

export default function DefectsDetails() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [dwellingType, setDwellingType] = useState("");
  const [dwellingCount, setDwellingCount] = useState(1);
  const [dwellings, setDwellings] = useState([]);
  const [closedOutMap, setClosedOutMap] = useState({});
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDwelling, setEditingDwelling] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Check if user is view_only (read-only mode)
  const isViewOnly = user?.role === "view_only";

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    if (siteId && sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(siteId);
    }
  }, [siteId, sites]);

  useEffect(() => {
    if (selectedSiteId) {
      loadDwellings();
    }
  }, [selectedSiteId]);

  const loadSites = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error("Error loading sites:", error);
      toast.error("Failed to load sites");
    }
  };

  const loadDwellings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/dwellings?site_id=${selectedSiteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDwellings(data);

        // Load defects to compute closed-out counts per dwelling
        try {
          const defectsRes = await fetch(`${API}/defects?site_id=${selectedSiteId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (defectsRes.ok) {
            const defectsData = await defectsRes.json();
            const map = {};
            defectsData.forEach((d) => {
              if (d.closed_out) {
                map[d.dwelling_id] = (map[d.dwelling_id] || 0) + 1;
              }
            });
            setClosedOutMap(map);
          }
        } catch (e) {
          console.error("Error loading defects for closed-out counts:", e);
        }
      }
    } catch (error) {
      console.error("Error loading dwellings:", error);
      toast.error("Failed to load dwellings");
    } finally {
      setLoading(false);
    }
  };

  // Natural sort function for proper numeric ordering
  const naturalSort = (a, b) => {
    const ax = [];
    const bx = [];
    
    a.dwelling_number.replace(/(\d+)|(\D+)/g, (_, num, str) => {
      ax.push([num || Infinity, str || ""]);
    });
    
    b.dwelling_number.replace(/(\d+)|(\D+)/g, (_, num, str) => {
      bx.push([num || Infinity, str || ""]);
    });
    
    while (ax.length && bx.length) {
      const an = ax.shift();
      const bn = bx.shift();
      const nn = (an[0] - bn[0]) || an[1].localeCompare(bn[1]);
      if (nn) return nn;
    }
    
    return ax.length - bx.length;
  };

  const handleSetupDwellings = async () => {
    if (!dwellingType) {
      toast.error("Please select a dwelling type");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const promises = [];

      for (let i = 1; i <= dwellingCount; i++) {
        let dwellingNumber;
        if (dwellingType === "single") {
          dwellingNumber = "Single Dwelling";
        } else if (dwellingType === "townhouse") {
          dwellingNumber = `Townhouse ${i}`;
        } else {
          dwellingNumber = `Apartment ${i}`;
        }

        const response = fetch(`${API}/dwellings`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            site_id: selectedSiteId,
            dwelling_type: dwellingType,
            dwelling_number: dwellingNumber
          })
        });
        promises.push(response);
      }

      await Promise.all(promises);
      toast.success(`${dwellingCount} dwelling(s) created successfully!`);
      setShowSetupDialog(false);
      setDwellingType("");
      setDwellingCount(1);
      loadDwellings();
    } catch (error) {
      console.error("Error creating dwellings:", error);
      toast.error("Failed to create dwellings");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDwelling = async (dwellingId) => {
    // Check if dwelling has defects
    const dwelling = dwellings.find(d => d.id === dwellingId);
    if (dwelling && dwelling.total_defects > 0) {
      toast.error("Cannot delete dwelling with existing defects. Please remove all defects first.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this dwelling?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/dwellings/${dwellingId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Dwelling deleted successfully");
        loadDwellings();
      }
    } catch (error) {
      console.error("Error deleting dwelling:", error);
      toast.error("Failed to delete dwelling");
    }
  };

  const handleAddSequentialDwelling = async () => {
    if (dwellings.length === 0) {
      toast.error("No dwellings found. Please setup dwellings first.");
      return;
    }

    // Get the dwelling type from existing dwellings
    const dwellingType = dwellings[0].dwelling_type;
    
    // Find the highest number
    const numbers = dwellings.map(d => {
      const match = d.dwelling_number.match(/\d+/);
      return match ? parseInt(match[0]) : 0;
    });
    const nextNumber = Math.max(...numbers) + 1;

    let dwellingNumber;
    if (dwellingType === "single") {
      toast.error("Cannot add additional dwellings to a single dwelling site.");
      return;
    } else if (dwellingType === "townhouse") {
      dwellingNumber = `Townhouse ${nextNumber}`;
    } else {
      dwellingNumber = `Apartment ${nextNumber}`;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/dwellings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          site_id: selectedSiteId,
          dwelling_type: dwellingType,
          dwelling_number: dwellingNumber
        })
      });

      if (response.ok) {
        toast.success(`${dwellingNumber} added successfully!`);
        loadDwellings();
      }
    } catch (error) {
      console.error("Error adding dwelling:", error);
      toast.error("Failed to add dwelling");
    } finally {
      setLoading(false);
    }
  };

  const handleEditDwelling = (dwelling) => {
    setEditingDwelling({
      id: dwelling.id,
      dwelling_number: dwelling.dwelling_number,
      address: dwelling.address || ""
    });
    setShowEditDialog(true);
  };

  const handleUpdateDwelling = async () => {
    if (!editingDwelling.dwelling_number.trim()) {
      toast.error("Dwelling number is required");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/dwellings/${editingDwelling.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          dwelling_number: editingDwelling.dwelling_number,
          address: editingDwelling.address
        })
      });

      if (response.ok) {
        toast.success("Dwelling updated successfully!");
        setShowEditDialog(false);
        setEditingDwelling(null);
        loadDwellings();
      } else {
        toast.error("Failed to update dwelling");
      }
    } catch (error) {
      console.error("Error updating dwelling:", error);
      toast.error("Failed to update dwelling");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate("/defects")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="heading-primary text-2xl sm:text-3xl">Defects Details</h1>
            <p className="text-slate-600">Manage defects by dwelling</p>
          </div>
        </div>
      </div>

      {/* Site Selection */}
      <Card className="dashboard-card mb-6">
        <CardHeader>
          <CardTitle>Select Site</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label>Site</Label>
              <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedSiteId && dwellings.length === 0 && !isViewOnly && (
              <div className="flex items-end">
                <Button onClick={() => setShowSetupDialog(true)} className="btn-construction">
                  <Plus className="w-4 h-4 mr-2" />
                  Setup Dwellings
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Site Information & Permissions */}
      {selectedSiteId && (
        <Card className="dashboard-card mb-6 border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Site Details */}
              <div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">
                  {sites.find(s => s.id === selectedSiteId)?.name}
                </h3>
                <p className="text-sm text-slate-600">
                  {sites.find(s => s.id === selectedSiteId)?.address}
                </p>
              </div>

              {/* Quick Stats */}
              {dwellings.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-4 pt-4 border-t border-slate-200">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Total Dwellings</p>
                    <p className="text-2xl font-bold text-slate-900">{dwellings.length}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Total Defects</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {dwellings.reduce((sum, d) => sum + d.total_defects, 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">New</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {dwellings.reduce((sum, d) => sum + (d.total_defects - d.in_progress_defects - d.completed_defects), 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">In Progress</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {dwellings.reduce((sum, d) => sum + d.in_progress_defects, 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Completed</p>
                    <p className="text-2xl font-bold text-green-600">
                      {dwellings.reduce((sum, d) => sum + d.completed_defects, 0) -
                        Object.values(closedOutMap).reduce((a, b) => a + b, 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Closed Out</p>
                    <p className="text-2xl font-bold text-slate-800" data-testid="details-closedout-total">
                      {Object.values(closedOutMap).reduce((a, b) => a + b, 0)}
                    </p>
                  </div>
                </div>
              )}

              {/* Permissions Message */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-start space-x-3 bg-blue-50 p-4 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-1">Site Access & Permissions</h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>
                        <strong>Your Role:</strong> {user?.role === "admin" ? "Administrator" : user?.role === "supervisor" ? "Supervisor" : "Site Manager"}
                      </p>
                      {user?.role === "admin" ? (
                        <p className="text-blue-700">
                          ✓ You have full access to all sites and can manage defects, dwellings, and user permissions.
                        </p>
                      ) : user?.role === "supervisor" ? (
                        <p className="text-blue-700">
                          ✓ You have access to all sites and can manage defects and dwellings.
                        </p>
                      ) : (
                        <>
                          <p className="text-blue-700">
                            ✓ You can input and manage defects for sites you are assigned to.
                          </p>
                          <p className="text-blue-700 mt-2">
                            <strong>Note:</strong> If you are an assistant site manager and don't see a site listed, 
                            you will need an administrator to allocate that site to your account.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dwellings List */}
      {selectedSiteId && (
        <Card className="dashboard-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Dwellings</CardTitle>
              <p className="text-sm text-slate-600">
                Click on any dwelling to view and add defects
              </p>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
              </div>
            ) : dwellings.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Home className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No dwellings found</p>
                <p className="text-sm">Click "Add Dwellings" to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-100 border-b-2 border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Dwelling</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Total</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">New</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">In Progress</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Completed</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Closed Out</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Defects</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {[...dwellings].sort(naturalSort).map((dwelling) => {
                      const newDefects = dwelling.total_defects - dwelling.completed_defects - dwelling.in_progress_defects;
                      return (
                        <tr
                          key={dwelling.id}
                          className="hover:bg-blue-50 hover:shadow-sm transition-all cursor-pointer border-l-4 border-l-transparent hover:border-l-blue-500"
                          onClick={() => navigate(`/defects/${selectedSiteId}/dwelling/${dwelling.id}`)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              {dwelling.dwelling_type === "single" ? (
                                <Home className="w-4 h-4 text-blue-500" />
                              ) : (
                                <Building className="w-4 h-4 text-purple-500" />
                              )}
                              <span className="text-sm font-medium text-slate-900">
                                {dwelling.dwelling_number}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm font-semibold text-slate-900">
                              {dwelling.total_defects}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <AlertCircle className="w-3 h-3 text-red-500" />
                              <span className="text-sm font-semibold text-red-600">
                                {newDefects}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Clock className="w-3 h-3 text-amber-500" />
                              <span className="text-sm font-semibold text-amber-600">
                                {dwelling.in_progress_defects}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              <span className="text-sm font-semibold text-green-600">
                                {dwelling.completed_defects - (closedOutMap[dwelling.id] || 0)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Lock className="w-3 h-3 text-slate-500" />
                              <span className="text-sm font-semibold text-slate-800">
                                {closedOutMap[dwelling.id] || 0}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/defects/${selectedSiteId}/dwelling/${dwelling.id}`);
                              }}
                              className="bg-blue-500 text-white hover:bg-blue-600 border-0"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              {dwelling.total_defects > 0 ? "View" : "Add"} Defects
                            </Button>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {!isViewOnly && (
                              <div className="flex items-center justify-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditDwelling(dwelling);
                                  }}
                                  className="text-blue-500 hover:text-blue-700"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteDwelling(dwelling.id);
                                  }}
                                  disabled={dwelling.total_defects > 0}
                                  className={`${
                                    dwelling.total_defects > 0
                                      ? "text-gray-300 cursor-not-allowed"
                                      : "text-red-500 hover:text-red-700"
                                  }`}
                                  title={dwelling.total_defects > 0 ? "Cannot delete dwelling with existing defects" : "Delete dwelling"}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {dwellings.length > 0 && dwellings[0].dwelling_type !== "single" && !isViewOnly && (
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={handleAddSequentialDwelling}
                  variant="outline"
                  className="flex items-center gap-2"
                  disabled={loading}
                >
                  <Plus className="w-4 h-4" />
                  Add Next {dwellings[0].dwelling_type === "townhouse" ? "Townhouse" : "Apartment"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Dwellings</DialogTitle>
            <DialogDescription>
              Configure dwellings for this site by selecting the type and quantity.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Dwelling Type</Label>
              <Select value={dwellingType} onValueChange={setDwellingType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single Dwelling Site</SelectItem>
                  <SelectItem value="townhouse">Multi Dwelling Townhouse</SelectItem>
                  <SelectItem value="apartment">Apartment Block</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {dwellingType && dwellingType !== "single" && (
              <div>
                <Label>Number of {dwellingType === "townhouse" ? "Townhouses" : "Apartments"}</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={dwellingCount}
                  onChange={(e) => setDwellingCount(parseInt(e.target.value) || 1)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSetupDwellings} disabled={loading}>
              {loading ? "Creating..." : "Create Dwellings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dwelling Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Dwelling</DialogTitle>
            <DialogDescription>
              Update the details of this dwelling.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Dwelling Number/Code *</Label>
              <Input
                placeholder="e.g., Apartment 101, Unit A, ESP-606"
                value={editingDwelling?.dwelling_number || ""}
                onChange={(e) => setEditingDwelling(prev => ({
                  ...prev,
                  dwelling_number: e.target.value
                }))}
              />
              <p className="text-xs text-slate-500 mt-1">
                This will update the dwelling number on all existing defects
              </p>
            </div>
            <div>
              <Label>Address (Optional)</Label>
              <Input
                placeholder="e.g., 123 Main St, Building A"
                value={editingDwelling?.address || ""}
                onChange={(e) => setEditingDwelling(prev => ({
                  ...prev,
                  address: e.target.value
                }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateDwelling} disabled={loading}>
              {loading ? "Updating..." : "Update Dwelling"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
